import { useEffect, useRef } from 'react';
import { Link, NavLink, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart3, CalendarRange, LineChart, Network } from 'lucide-react';

const TAB_ROUTES = [
  { path: 'plan', labelKey: 'sopModule.tabs.plan', icon: BarChart3 },
  { path: 'cycles', labelKey: 'sopModule.tabs.cycles', icon: CalendarRange },
  { path: 'scenarios', labelKey: 'sopModule.tabs.scenarios', icon: Network },
] as const;

function tabClass(isActive: boolean): string {
  return [
    'inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
    isActive
      ? 'border-stocker-primary text-stocker-primary'
      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900',
  ].join(' ');
}

/** Tabs fijos; el scroll vive solo en el panel del tab activo (mismo patrón que Forecast). */
export function SopModuleLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const panelRef = useRef<HTMLDivElement>(null);
  const cycleQuery = searchParams.toString();
  const suffix = cycleQuery ? `?${cycleQuery}` : '';
  const basePath = '/demand-planning/sop';

  useEffect(() => {
    panelRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 shadow-sm md:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <nav className="-mb-px flex flex-wrap gap-1" aria-label={t('sopModule.tabsAria')}>
            {TAB_ROUTES.map(({ path, labelKey, icon: Icon }) => (
              <NavLink
                key={path}
                to={`${basePath}/${path}${suffix}`}
                className={({ isActive }) => tabClass(isActive)}
                end={path === 'plan'}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {t(labelKey)}
              </NavLink>
            ))}
          </nav>
          <Link
            to="/demand-planning/forecast"
            className="inline-flex shrink-0 items-center gap-1.5 py-2 text-xs font-medium text-gray-500 hover:text-stocker-primary"
          >
            <LineChart className="h-3.5 w-3.5" />
            {t('forecastPage.title')}
          </Link>
        </div>
      </div>
      <div ref={panelRef} className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
